import { logger, LOGS } from "../../../utils";
import type { EventBus } from "../../events";

/**
 * AB#1725 — Délai maximal d'attente de l'analyse initiale (1ère connexion) avant
 * bascule sur le Dashboard. Arbitrage PO du 22/07/2026 : 3 minutes.
 * (Historique : 30 s à l'origine — l'analyse complète durant plusieurs minutes,
 * le timeout gagnait systématiquement la course et le Dashboard s'affichait
 * pendant l'analyse, avec des compteurs qui bougent.)
 */
export const INITIAL_ANALYSIS_DASHBOARD_TIMEOUT_MS = 180_000;

/**
 * Attend la fin de l'analyse initiale des mails d'un nouvel utilisateur :
 * race entre l'événement 'initial-analysis-completed' (émis par MailManager en fin
 * d'analyse, Y COMPRIS en cas d'erreur — cf. MailManager.firstUserEmailsAnalysis)
 * et un timeout de sécurité — le premier des deux résout la promesse.
 *
 * Armée en DÉBUT de Phase 2 — à l'affichage de l'écran de bienvenue — pour que
 * le cap borne le temps d'affichage de l'écran, calcul de contexte (/me/context) inclus.
 * En tout état de cause, à armer AVANT mailManager._initialize() pour ne pas manquer une
 * émission synchrone de l'événement (ex : nouvel utilisateur sans licence).
 *
 * La promesse ne rejette jamais ; le listener et le timer sont nettoyés quelle
 * que soit la branche gagnante.
 */
export function waitForInitialAnalysis(
    eventBus: EventBus,
    timeoutMs: number = INITIAL_ANALYSIS_DASHBOARD_TIMEOUT_MS,
): Promise<void> {
    return new Promise<void>((resolve) => {
        let resolved = false;
        let timeoutId: ReturnType<typeof setTimeout>;

        const handler = () => {
            if (resolved) return;
            resolved = true;
            clearTimeout(timeoutId);
            eventBus.off("initial-analysis-completed", handler);
            logger.info(
                [LOGS.ServiceProvider, LOGS.AB1725],
                "Phase 3: initial-analysis-completed received → transition vers Phase 4",
            );
            resolve();
        };

        eventBus.on("initial-analysis-completed", handler);

        timeoutId = setTimeout(() => {
            if (resolved) return;
            resolved = true;
            eventBus.off("initial-analysis-completed", handler);
            logger.warn(
                [LOGS.ServiceProvider, LOGS.AB1725],
                `Phase 3: timeout ${timeoutMs / 1000}s atteint pour initial-analysis — transition vers Phase 4`,
            );
            resolve();
        }, timeoutMs);
    });
}
